'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { MapPin, ArrowRight, Compass } from 'lucide-react';
import { trpc } from '@/lib/trpc/client';
import { useTranslation } from 'react-i18next';

interface TravelGuidesSidebarProps {
  relatedGuideIds?: number[];
  countryName?: string; // Used to find guides if IDs not provided
}

export default function TravelGuidesSidebar({ relatedGuideIds, countryName }: TravelGuidesSidebarProps) {
  const { i18n, t } = useTranslation();
  const currentLang = (i18n.language || 'en') as 'en' | 'de' | 'uk';
  
  // Get all guides to filter from
  const { data: allGuides } = trpc.travelGuides.getAll.useQuery();
  
  // Filter guides based on relatedGuideIds or countryName
  const relatedGuides = React.useMemo(() => {
    if (!allGuides) return [];
    
    // If specific guide IDs provided, use those
    if (relatedGuideIds && relatedGuideIds.length > 0) {
      return allGuides.filter(guide => relatedGuideIds.includes(guide.id)).slice(0, 3);
    }
    
    // Otherwise, try to match by country name (handles multi-country trips)
    if (countryName) {
      // Split country name by common separators (comma, slash, and, &)
      const countries = countryName
        .split(/[,/&]|\sand\s/i)
        .map(c => c.trim().toLowerCase())
        .filter(c => c.length > 0);
      
      // Find guides that match any of the countries
      return allGuides
        .filter(guide => {
          const title = (guide.title as Record<string, string>)[currentLang] || (guide.title as Record<string, string>).en;
          const titleLower = title.toLowerCase();
          
          // Check if any of the countries in the multi-country trip match this guide
          return countries.some(country => 
            titleLower.includes(country) || country.includes(titleLower.replace(/\s*travel\s*guide\s*/i, '').trim())
          );
        })
        .slice(0, 3);
    }
    
    return [];
  }, [allGuides, relatedGuideIds, countryName, currentLang]);
  
  // If we have a matching guide, show it
  if (relatedGuides && relatedGuides.length > 0) {
    const guide = relatedGuides[0];
    const title = (guide.title as Record<string, string>)[currentLang] || (guide.title as Record<string, string>).en;
    const excerpt = (guide.excerpt as Record<string, string>)[currentLang] || (guide.excerpt as Record<string, string>).en;

    return (
      <Link
        href={`/${currentLang}/travel-guides/${guide.slug}`}
        target="_blank"
        rel="noopener noreferrer"
        className="block group"
      >
        <div className="bg-gradient-to-br from-primary-50 to-white rounded-2xl overflow-hidden border border-primary-100 shadow-sm hover:shadow-md transition-all duration-300">
          {/* Image with overlay */}
          <div className="relative h-48 overflow-hidden">
            {guide.thumbnailImage && (
              <Image
                src={guide.thumbnailImage}
                alt={title}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-300"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
            
            {/* Tag */}
            <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-full">
              <MapPin className="h-3.5 w-3.5 text-primary-600" />
              <span className="text-xs font-medium text-gray-800">
                {t('travelGuides.sidebar.title')}
              </span>
            </div>
            
            {/* Title on image */}
            <div className="absolute bottom-3 left-3 right-3">
              <h3 className="text-xl font-serif font-bold text-white drop-shadow-lg">
                {title}
              </h3>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-4">
            <p className="text-sm text-gray-700 line-clamp-2 mb-3 leading-relaxed">
              {excerpt}
            </p>
            
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-primary-600 group-hover:text-primary-700 transition-colors">
                {t('travelGuides.sidebar.readMore')}
              </span>
              <ArrowRight className="h-4 w-4 text-primary-600 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </div>
      </Link>
    );
  }
  
  // If no matching guide is found, show a generic section encouraging exploration
  return (
    <Link
      href={`/${currentLang}/travel-guides`}
      target="_blank"
      rel="noopener noreferrer"
      className="block group"
    >
      <div className="bg-gradient-to-br from-primary-50 to-white rounded-2xl overflow-hidden border border-primary-100 shadow-sm hover:shadow-md transition-all duration-300">
        {/* Header with icon */}
        <div className="relative h-32 bg-gradient-to-br from-primary-400 to-primary-600 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-4 right-4 rotate-12">
              <Compass className="h-24 w-24 text-white" />
            </div>
            <div className="absolute bottom-4 left-4 -rotate-12">
              <MapPin className="h-16 w-16 text-white" />
            </div>
          </div>
          
          {/* Tag */}
          <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-full">
            <Compass className="h-3.5 w-3.5 text-primary-600" />
            <span className="text-xs font-medium text-gray-800">
              {t('travelGuides.sidebar.title')}
            </span>
          </div>
          
          {/* Title */}
          <div className="absolute bottom-3 left-3 right-3">
            <h3 className="text-xl font-serif font-bold text-white drop-shadow-lg">
              {t('travelGuides.sidebar.exploreDestinations')}
            </h3>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4">
          <p className="text-sm text-gray-700 mb-3 leading-relaxed">
            {t('travelGuides.sidebar.exploreDescription')}
          </p>
          
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-primary-600 group-hover:text-primary-700 transition-colors">
              {t('travelGuides.sidebar.viewAll')}
            </span>
            <ArrowRight className="h-4 w-4 text-primary-600 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </Link>
  );
}

